import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  IAccountProfile,
  IAccountProfilePreferences,
  IAccountProfileSocialNetwork,
  IAccountUniqueProperties
} from '@core/models/account-profile.model';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  catchError,
  finalize,
  forkJoin,
  map,
  of,
  shareReplay,
  tap
} from 'rxjs';
import { IAccountOrganisation } from '../../models/auth.model';
import { ToastService } from '../toast';
import { getSafeErrorDetails } from '@core/utils';
import { IUnsplashPhoto, IUnsplashSearchResult, IUserAboutMePayload } from '@core/models';
import { UNSPLASH_API_URL } from '@core/constants';
import {
  IProfileResponse,
  mapProfileResponse,
  toAboutMeRequest,
  toPreferencesRequest,
  toSocialNetworksRequest
} from './account-profile.mapper';
import { Logger } from '../logger';

const log = new Logger('AccountService');

export interface IAccountAvailabilityResponse {
  isAvailable: boolean;
}

export interface IImageUploadResponse {
  url: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class AccountService {
  private _userDetails: BehaviorSubject<IAccountProfile | null> =
    new BehaviorSubject<IAccountProfile | null>(null);

  private _allOrganiztions: BehaviorSubject<IAccountOrganisation[]> = new BehaviorSubject<
    IAccountOrganisation[]
  >([]);

  private _profileLoad$: Observable<IAccountProfile | null> | null = null;

  get getUserDetails(): Observable<IAccountProfile | null> {
    return this._userDetails.asObservable();
  }

  set setUserDetails(value: IAccountProfile | null) {
    const currentValue = this._userDetails.getValue();
    const newValue = value ? { ...currentValue, ...value } : null;
    this._userDetails.next(newValue);
  }

  get organizations(): Observable<IAccountOrganisation[]> {
    return this._allOrganiztions.asObservable();
  }

  set organizations(value: IAccountOrganisation[]) {
    this._allOrganiztions.next(value);
  }

  constructor(private http: HttpClient, private toastService: ToastService) {}

  /**
   * @description `Get the list of organizations`
   */
  getOrganizations(): Observable<IAccountOrganisation[]> {
    return this.http.get<IAccountOrganisation[]>('/organizations');
  }

  /**
   * @description `Update the account mobile number`
   */
  updateMobileNumber(mobileNumber: string): Observable<void> {
    return this.http.put<void>('/account/mobile-number', { mobileNumber });
  }

  /**
   * @description `Load account profile details`
   */
  getProfile(): Observable<IAccountProfile> {
    return this.http.get<IProfileResponse>('/account/profile').pipe(map(mapProfileResponse));
  }

  loadProfileIfMissing(): Observable<IAccountProfile | null> {
    const currentProfile = this._userDetails.getValue();

    if (currentProfile) {
      return of(currentProfile);
    }

    // Share a single in-flight load so concurrent callers do not each fetch the profile.
    if (!this._profileLoad$) {
      this._profileLoad$ = this.getProfile().pipe(
        tap(profile => {
          this.setUserDetails = profile;
        }),
        catchError(error => {
          log.warn('Account profile load failed', getSafeErrorDetails(error));
          return of(null);
        }),
        finalize(() => {
          this._profileLoad$ = null;
        }),
        shareReplay(1)
      );
    }

    return this._profileLoad$;
  }

  updateSocialNetworks(socialNetwork: IAccountProfileSocialNetwork): Observable<void> {
    return this.http
      .put<void>('/account/social-networks', toSocialNetworksRequest(socialNetwork))
      .pipe(
        tap(() =>
          this.toastService.addAll([
            {
              severity: 'success',
              text: 'Your Social Networks changes have been successfully updated'
            }
          ])
        ),
        catchError(error => {
          log.warn('Social networks save failed', getSafeErrorDetails(error));
          this.toastService.addAll([
            {
              severity: 'error',
              text: 'There was a problem saving your Social Networks updates'
            }
          ]);

          return EMPTY;
        })
      );
  }

  updateAboutMe(aboutMe: IUserAboutMePayload): Observable<void> {
    return this.http.put<void>('/account/about-me', toAboutMeRequest(aboutMe)).pipe(
      tap(() =>
        this.toastService.addAll([
          { severity: 'success', text: 'Your About Me changes have been successfully updated' }
        ])
      ),
      catchError(error => {
        log.warn('About me save failed', getSafeErrorDetails(error));
        this.toastService.addAll([
          { severity: 'error', text: 'There was a problem saving your About Me updates' }
        ]);

        return EMPTY;
      })
    );
  }

  uploadProfilePicture(file: Blob): Observable<IImageUploadResponse> {
    const formData = new FormData();
    formData.append('file', file, 'profile-picture');

    return this.http.put<IImageUploadResponse>('/account/profile-picture', formData).pipe(
      tap(() =>
        this.toastService.addAll([
          { severity: 'success', text: 'Your profile image has been successfully updated' }
        ])
      ),
      catchError(error => {
        log.warn('Profile picture upload failed', getSafeErrorDetails(error));
        this.toastService.addAll([
          { severity: 'error', text: 'There was a problem saving your profile image' }
        ]);

        return EMPTY;
      })
    );
  }

  uploadBanner(file: Blob): Observable<IImageUploadResponse> {
    const formData = new FormData();
    formData.append('file', file, 'banner');

    return this.http.put<IImageUploadResponse>('/account/banner', formData).pipe(
      tap(() =>
        this.toastService.addAll([
          { severity: 'success', text: 'Your banner image has been successfully updated' }
        ])
      ),
      catchError(error => {
        log.warn('Banner upload failed', getSafeErrorDetails(error));
        this.toastService.addAll([
          { severity: 'error', text: 'There was a problem saving your banner image' }
        ]);

        return EMPTY;
      })
    );
  }

  updatePreferences(
    preferences: IAccountProfilePreferences,
    secondaryEmails: string[]
  ): Observable<void> {
    return forkJoin([
      this.http.put<void>('/account/preferences', toPreferencesRequest(preferences)),
      this.http.put<void>('/account/emails', { emails: secondaryEmails })
    ]).pipe(
      tap(() =>
        this.toastService.addAll([
          { severity: 'success', text: 'Your Preferences have been successfully updated' }
        ])
      ),
      map(() => undefined),
      catchError(error => {
        log.warn('Preferences save failed', getSafeErrorDetails(error));
        this.toastService.addAll([
          { severity: 'error', text: 'There was a problem saving your Preferences updates' }
        ]);

        return EMPTY;
      })
    );
  }

  /**
   * @description `Check whether an email is available`
   */
  checkAvailability(payload: IAccountUniqueProperties): Observable<IAccountAvailabilityResponse> {
    return this.http.post<IAccountAvailabilityResponse>('/account/availability', payload);
  }

  /**
   * @description `Update the account primary email`
   */
  updateEmail(email: string): Observable<void> {
    return this.http.put<void>('/account/email', { email });
  }

  /**
   * @description `Make an unsplash api request for listing photos`
   */
  getUnsplashPhotos(): Observable<IUnsplashPhoto[]> {
    return this.http.get<IUnsplashPhoto[]>(UNSPLASH_API_URL.PHOTOS);
  }

  /**
   * @description `Make an unsplash search api request by term`
   */
  getUnsplashSearchPhotos(term: string): Observable<IUnsplashSearchResult> {
    return this.http.get<IUnsplashSearchResult>(UNSPLASH_API_URL.SEARCH_PHOTOS, {
      params: { query: term }
    });
  }
}
